import { DT, JUMP_VELOCITY, PLAYER, SELF_CANNON, WORLD } from "./config.ts";
import { clamp, localToWorld, worldToLocal, wrapAngle, type Pose, type Vec3 } from "./math.ts";
import { SHIP_COLLIDERS, overDeck } from "./ship.ts";
import { COLLIDERS, ISLAND_CANNONS, terrainHeight, type Collider, type IslandId } from "./world.ts";

/**
 * Fixed-step player movement, run identically by the server (authority) and
 * the client (prediction of the local player only).
 */

export const FRAME_WORLD = 0;
export const MODE_WALK = 0;
export const MODE_SWIM = 1;
export const MODE_LAUNCH = 2;

/** Body frame for standing on the ship of crew slot `slot` (frame 0 is the world). */
export function shipFrame(slot: number): number {
  return slot + 1;
}

/** Crew slot of the ship a frame refers to; -1 for the world frame. */
export function frameSlot(frame: number): number {
  return frame - 1;
}

/** Ship poses indexed by crew slot; null where no ship exists. */
export type ShipPoses = readonly (Pose | null)[];

export interface Body {
  /** FRAME_WORLD, or shipFrame(slot) for ship-local coordinates on that ship. */
  frame: number;
  /** Feet position. y is identical in both frames (the ship does not pitch or heave). */
  x: number;
  y: number;
  z: number;
  vy: number;
  vx: number;
  vz: number;
  launchTicks: number;
  /** Yaw in the body's frame (world yaw, or yaw relative to the ship heading). */
  yaw: number;
  pitch: number;
  grounded: boolean;
  mode: number;
  /** Highest y since last grounded, for fall damage. */
  fallStartY: number;
  breath: number;
  crouched: boolean;
  sprinting: boolean;
  sprintTime: number;
  slideRemaining: number;
  /** Fixed entry movement heading, expressed in the current body frame. */
  slideYaw: number;
  slideCooldown: number;
  crouchHeld: boolean;
}

export interface MoveInput {
  /** Strafe, -1 … 1. */
  mx: number;
  /** Forward, -1 … 1. */
  mz: number;
  yaw: number;
  pitch: number;
  /** Frame the yaw is expressed in. */
  yawFrame: number;
  jump: boolean;
  dive: boolean;
  sprint: boolean;
  crouch: boolean;
  scope: boolean;
}

export interface StepEnv {
  ships: ShipPoses;
  carrying: boolean;
  sprintAllowed: boolean;
  /** Isolated collision fixtures; production uses the shared world colliders. */
  worldColliders?: readonly Collider[];
}

export type MovementContext = Pick<StepEnv, "carrying" | "sprintAllowed">;

/** A carried chest or a full bucket: walk at PLAYER.carrySpeed, no sprint or slide. */
export function carriesLoad(p: { carrying: string; equipment: string; bucketFull: boolean }): boolean {
  return p.carrying !== "" || (p.equipment === "bucket" && p.bucketFull);
}

export interface StepEvents {
  /** Metres fallen when landing this step (0 when no landing). */
  fall: number;
  /** Seconds this step spent with zero breath. */
  drowning: number;
  enteredWater: boolean;
  /** Frame changed this step. */
  frameChanged: boolean;
}

export const ZERO_INPUT: Readonly<MoveInput> = { mx: 0, mz: 0, yaw: 0, pitch: 0, yawFrame: FRAME_WORLD, jump: false, dive: false, sprint: false, crouch: false, scope: false };

export function createBody(frame: number, x: number, y: number, z: number, yaw = 0): Body {
  return { frame, x, y, z, vx: 0, vy: 0, vz: 0, launchTicks: 0, yaw, pitch: 0, grounded: true, mode: MODE_WALK, fallStartY: y, breath: PLAYER.breathSeconds,
    crouched: false, sprinting: false, sprintTime: 0, slideRemaining: 0, slideYaw: 0, slideCooldown: 0, crouchHeld: false };
}

export function copyBody(src: Body, dst: Body): Body {
  dst.frame = src.frame;
  dst.x = src.x;
  dst.y = src.y;
  dst.z = src.z;
  dst.vy = src.vy;
  dst.vx = src.vx;
  dst.vz = src.vz;
  dst.launchTicks = src.launchTicks;
  dst.yaw = src.yaw;
  dst.pitch = src.pitch;
  dst.grounded = src.grounded;
  dst.mode = src.mode;
  dst.fallStartY = src.fallStartY;
  dst.breath = src.breath;
  dst.crouched = src.crouched;
  dst.sprinting = src.sprinting;
  dst.sprintTime = src.sprintTime;
  dst.slideRemaining = src.slideRemaining;
  dst.slideYaw = src.slideYaw;
  dst.slideCooldown = src.slideCooldown;
  dst.crouchHeld = src.crouchHeld;
  return dst;
}

const R = PLAYER.radius;
const STEP = PLAYER.stepHeight;
const SHIP_NEAR = 12;
const BOUND = WORLD.half - 2;
const tmp: Vec3 = { x: 0, y: 0, z: 0 };
const startWorld: Vec3 = { x: 0, y: 0, z: 0 };
const endWorld: Vec3 = { x: 0, y: 0, z: 0 };
const localStart: Vec3 = { x: 0, y: 0, z: 0 };
const localEnd: Vec3 = { x: 0, y: 0, z: 0 };
const CLEARANCE = 0.001;

export function bodyHeight(b: Pick<Body, "crouched">): number {
  return b.crouched ? PLAYER.crouchHeight : PLAYER.height;
}

/** Physical eye; the lower slide eye is cosmetic, not the interaction origin. */
export function bodyEyeHeight(b: Pick<Body, "crouched" | "mode">): number {
  return b.mode === MODE_SWIM ? 1.45 : b.crouched ? PLAYER.crouchEyeHeight : PLAYER.eyeHeight;
}

function footprintOverlaps(x: number, z: number, c: Collider): boolean {
  if (c.kind === "cyl") return (x - c.x) ** 2 + (z - c.z) ** 2 < (R + c.r) ** 2;
  return (x - clamp(x, c.minX, c.maxX)) ** 2 + (z - clamp(z, c.minZ, c.maxZ)) ** 2 < R * R;
}

/** Touching a floor/ceiling is not an overlap; tolerate 1 mm of numerical drift. */
export function capsuleFits(feet: Vec3, height: number, shapes: readonly Collider[]): boolean {
  for (const c of shapes) {
    if (feet.y + height <= c.y0 + CLEARANCE || feet.y >= c.y1 - CLEARANCE) continue;
    if (footprintOverlaps(feet.x, feet.z, c)) return false;
  }
  return true;
}

function standingFits(b: Body, ships: ShipPoses, shapes: readonly Collider[]): boolean {
  bodyWorld(b, ships, endWorld);
  if (!capsuleFits(endWorld, PLAYER.height, shapes)) return false;
  for (const ship of ships) {
    if (!ship || Math.abs(endWorld.x - ship.x) >= SHIP_NEAR || Math.abs(endWorld.z - ship.z) >= SHIP_NEAR) continue;
    worldToLocal(ship, endWorld.x, endWorld.y, endWorld.z, tmp);
    if (!capsuleFits(tmp, PLAYER.height, SHIP_COLLIDERS)) return false;
  }
  return true;
}

function endSlide(b: Body): void {
  if (b.slideRemaining <= 0) return;
  b.slideRemaining = 0;
  b.slideCooldown = PLAYER.slideCooldown;
  b.sprintTime = 0;
}

function resetLocomotion(b: Body): void {
  b.crouched = b.sprinting = b.crouchHeld = false;
  b.sprintTime = b.slideRemaining = b.slideYaw = b.slideCooldown = 0;
}

function pointSegmentDistanceSq(x: number, z: number, ax: number, az: number, bx: number, bz: number): number {
  const dx = bx - ax, dz = bz - az;
  const lengthSq = dx * dx + dz * dz;
  const t = lengthSq > 1e-12 ? clamp(((x - ax) * dx + (z - az) * dz) / lengthSq, 0, 1) : 0;
  return (x - ax - dx * t) ** 2 + (z - az - dz * t) ** 2;
}

/** Circle swept along a segment against the actual rounded box/cylinder footprint. */
function sweptFootprint(ax: number, az: number, bx: number, bz: number, c: Collider): boolean {
  if (c.kind === "cyl") return pointSegmentDistanceSq(c.x, c.z, ax, az, bx, bz) < (R + c.r) ** 2;
  if (footprintOverlaps(ax, az, c) || footprintOverlaps(bx, bz, c)) return true;
  const dx = bx - ax, dz = bz - az;
  let enter = 0, exit = 1;
  if (Math.abs(dx) < 1e-12) {
    if (ax < c.minX || ax > c.maxX) exit = -1;
  } else {
    const a = (c.minX - ax) / dx, b = (c.maxX - ax) / dx;
    enter = Math.max(enter, Math.min(a, b));
    exit = Math.min(exit, Math.max(a, b));
  }
  if (enter <= exit) {
    const za = az + dz * enter, zb = az + dz * exit;
    if (Math.min(za, zb) < c.maxZ + R && Math.max(za, zb) > c.minZ - R) return true;
  }
  enter = 0; exit = 1;
  if (Math.abs(dz) < 1e-12) {
    if (az < c.minZ || az > c.maxZ) exit = -1;
  } else {
    const a = (c.minZ - az) / dz, b = (c.maxZ - az) / dz;
    enter = Math.max(enter, Math.min(a, b));
    exit = Math.min(exit, Math.max(a, b));
  }
  if (enter <= exit) {
    const xa = ax + dx * enter, xb = ax + dx * exit;
    if (Math.min(xa, xb) < c.maxX + R && Math.max(xa, xb) > c.minX - R) return true;
  }
  return pointSegmentDistanceSq(c.minX, c.minZ, ax, az, bx, bz) < R * R
    || pointSegmentDistanceSq(c.minX, c.maxZ, ax, az, bx, bz) < R * R
    || pointSegmentDistanceSq(c.maxX, c.minZ, ax, az, bx, bz) < R * R
    || pointSegmentDistanceSq(c.maxX, c.maxZ, ax, az, bx, bz) < R * R;
}

function headSweep(from: Vec3, to: Vec3, height: number, shapes: readonly Collider[]): number {
  let feet = to.y;
  if (to.y <= from.y) return feet;
  for (const c of shapes) {
    if (from.y + height > c.y0 + CLEARANCE || to.y + height <= c.y0) continue;
    const t = clamp((c.y0 - height - from.y) / (to.y - from.y), 0, 1);
    const x = from.x + (to.x - from.x) * t, z = from.z + (to.z - from.z) * t;
    if (sweptFootprint(x, z, to.x, to.z, c)) feet = Math.min(feet, c.y0 - height);
  }
  return feet;
}

/**
 * Pushes the capsule (circle radius R, current height at feet y) out of every shape
 * in `shapes` it overlaps sideways. Shapes whose top is within a step of the
 * feet are treated as floor and skipped (the support pass handles them).
 */
function resolveSideways(p: Vec3, shapes: readonly Collider[], height: number, stepHeight: number = STEP): void {
  for (const c of shapes) {
    if (p.y + height <= c.y0 + CLEARANCE || p.y >= c.y1 - stepHeight) continue;
    if (c.kind === "cyl") {
      const dx = p.x - c.x;
      const dz = p.z - c.z;
      const min = R + c.r;
      const d2 = dx * dx + dz * dz;
      if (d2 >= min * min) continue;
      const d = Math.sqrt(d2);
      if (d < 1e-6) {
        p.x = c.x + min;
      } else {
        p.x = c.x + (dx / d) * min;
        p.z = c.z + (dz / d) * min;
      }
    } else {
      const qx = clamp(p.x, c.minX, c.maxX);
      const qz = clamp(p.z, c.minZ, c.maxZ);
      const dx = p.x - qx;
      const dz = p.z - qz;
      const d2 = dx * dx + dz * dz;
      if (d2 >= R * R) continue;
      if (d2 > 1e-10) {
        const d = Math.sqrt(d2);
        p.x = qx + (dx / d) * R;
        p.z = qz + (dz / d) * R;
      } else {
        // Centre inside the box footprint: leave by the shallowest side.
        const left = p.x - c.minX;
        const right = c.maxX - p.x;
        const back = p.z - c.minZ;
        const front = c.maxZ - p.z;
        const m = Math.min(left, right, back, front);
        if (m === left) p.x = c.minX - R;
        else if (m === right) p.x = c.maxX + R;
        else if (m === back) p.z = c.minZ - R;
        else p.z = c.maxZ + R;
      }
    }
  }
}

/** Highest shape top under (x, z) that the feet can stand on (≤ y + STEP). */
function shapeSupport(x: number, y: number, z: number, shapes: readonly Collider[], best: number): number {
  for (const c of shapes) {
    if (c.y1 > y + STEP || c.y1 <= best) continue;
    if (c.kind === "cyl") {
      const dx = x - c.x;
      const dz = z - c.z;
      if (dx * dx + dz * dz <= c.r * c.r) best = c.y1;
    } else if (x >= c.minX && x <= c.maxX && z >= c.minZ && z <= c.maxZ) {
      best = c.y1;
    }
  }
  return best;
}

/** Deck support under a ship-local point, or -Infinity. */
function deckSupport(lx: number, y: number, lz: number): number {
  return shapeSupport(lx, y, lz, SHIP_COLLIDERS, -Infinity);
}

function toWorldFrame(b: Body, ship: Pose): void {
  localToWorld(ship, b.x, b.y, b.z, tmp);
  b.x = tmp.x;
  b.z = tmp.z;
  b.yaw = wrapAngle(b.yaw + ship.heading);
  b.slideYaw = wrapAngle(b.slideYaw + ship.heading);
  b.frame = FRAME_WORLD;
}

function toShipFrame(b: Body, ship: Pose, slot: number): void {
  worldToLocal(ship, b.x, b.y, b.z, tmp);
  b.x = tmp.x;
  b.z = tmp.z;
  b.yaw = wrapAngle(b.yaw - ship.heading);
  b.slideYaw = wrapAngle(b.slideYaw - ship.heading);
  b.frame = shipFrame(slot);
}

/** Moves a body onto the deck of ship `slot`; used by the ladder, stations and respawns. */
export function placeOnShip(b: Body, slot: number, lx: number, ly: number, lz: number, yaw = 0): void {
  b.frame = shipFrame(slot);
  b.x = lx;
  b.y = ly;
  b.z = lz;
  b.vy = 0;
  b.vx = b.vz = b.launchTicks = 0;
  b.yaw = yaw;
  b.grounded = true;
  b.mode = MODE_WALK;
  b.fallStartY = ly;
  resetLocomotion(b);
}

export function placeInWorld(b: Body, x: number, y: number, z: number, yaw = 0): void {
  b.frame = FRAME_WORLD;
  b.x = x;
  b.y = y;
  b.z = z;
  b.vy = 0;
  b.vx = b.vz = b.launchTicks = 0;
  b.yaw = yaw;
  b.grounded = true;
  b.mode = MODE_WALK;
  b.fallStartY = y;
  resetLocomotion(b);
}

/** Detaches the capsule into a world-frame, unsteerable cannon flight. */
export function beginPlayerLaunch(b: Body, feet: Vec3, velocity: Vec3, yawWorld: number, pitch: number): void {
  b.frame = FRAME_WORLD;
  b.x = feet.x;
  b.y = feet.y;
  b.z = feet.z;
  b.vx = velocity.x;
  b.vy = velocity.y;
  b.vz = velocity.z;
  b.launchTicks = 0;
  b.yaw = wrapAngle(yawWorld);
  b.pitch = clamp(pitch, -PLAYER.maxPitch, PLAYER.maxPitch);
  b.grounded = false;
  b.mode = MODE_LAUNCH;
  b.fallStartY = feet.y;
  resetLocomotion(b);
}

/** Validates extraction only; subsequent flight never ignores its source hull. */
export function playerLaunchExitClear(
  pivot: Vec3, feet: Vec3, ships: ShipPoses, sourceShipSlot = -1,
  sourceIslandId?: IslandId, worldColliders: readonly Collider[] = COLLIDERS,
): boolean {
  let carriage: Collider | undefined;
  if (sourceIslandId !== undefined) {
    for (let i = 0; i < ISLAND_CANNONS.length; i++) {
      if (ISLAND_CANNONS[i]!.id === sourceIslandId) {
        carriage = COLLIDERS[COLLIDERS.length - ISLAND_CANNONS.length + i];
        break;
      }
    }
  }
  const startY = pivot.y - PLAYER.height / 2;
  const dx = feet.x - pivot.x, dy = feet.y - startY, dz = feet.z - pivot.z;
  const count = Math.max(1, Math.ceil(Math.hypot(dx, dy, dz) / SELF_CANNON.maxStepDistance));
  for (let i = 0; i <= count; i++) {
    const t = i / count;
    tmp.x = pivot.x + dx * t;
    tmp.y = startY + dy * t;
    tmp.z = pivot.z + dz * t;
    if (Math.abs(tmp.x) > BOUND || Math.abs(tmp.z) > BOUND || tmp.y < terrainHeight(tmp.x, tmp.z) - CLEARANCE) return false;
    if (i === count || carriage === undefined) {
      if (!capsuleFits(tmp, PLAYER.height, worldColliders)) return false;
    } else {
      for (const c of worldColliders) {
        if (c === carriage || tmp.y + PLAYER.height <= c.y0 + CLEARANCE || tmp.y >= c.y1 - CLEARANCE) continue;
        if (footprintOverlaps(tmp.x, tmp.z, c)) return false;
      }
    }
    if (i > 0) {
      const previous = (i - 1) / count;
      startWorld.x = pivot.x + dx * previous;
      startWorld.y = startY + dy * previous;
      startWorld.z = pivot.z + dz * previous;
      // Sweep non-source solids as well, so a tiny corner graze cannot hide
      // between samples. The source must fit at the final endpoint above.
      for (const c of worldColliders) {
        if (c !== carriage && launchSideEntry(startWorld, tmp, c) <= 1) return false;
      }
    }
    for (let slot = 0; slot < ships.length; slot++) {
      const ship = ships[slot];
      if (!ship || (i < count && slot === sourceShipSlot)
        || Math.abs(tmp.x - ship.x) >= SHIP_NEAR || Math.abs(tmp.z - ship.z) >= SHIP_NEAR) continue;
      worldToLocal(ship, tmp.x, tmp.y, tmp.z, localEnd);
      if (!capsuleFits(localEnd, PLAYER.height, SHIP_COLLIDERS)) return false;
      if (i > 0 && slot !== sourceShipSlot) {
        worldToLocal(ship, startWorld.x, startWorld.y, startWorld.z, localStart);
        for (const c of SHIP_COLLIDERS) {
          if (launchSideEntry(localStart, localEnd, c) <= 1) return false;
        }
      }
    }
  }
  return true;
}

/** World position of a body's feet. */
export function bodyWorld(b: Body, ships: ShipPoses, out: Vec3): Vec3 {
  const ship = b.frame > FRAME_WORLD ? ships[frameSlot(b.frame)] : null;
  if (ship) return localToWorld(ship, b.x, b.y, b.z, out);
  out.x = b.x;
  out.y = b.y;
  out.z = b.z;
  return out;
}

/** World yaw of a body. */
export function bodyWorldYaw(b: Body, ships: ShipPoses): number {
  const ship = b.frame > FRAME_WORLD ? ships[frameSlot(b.frame)] : null;
  return ship ? wrapAngle(b.yaw + ship.heading) : b.yaw;
}

/**
 * Advances `b` by one fixed step (DT). Mutates `b`, fills and returns `ev`.
 */
export function stepBody(b: Body, input: MoveInput, env: StepEnv, ev: StepEvents): StepEvents {
  const dt = DT;
  const ships = env.ships;
  const shapes = env.worldColliders ?? COLLIDERS;
  b.slideCooldown = Math.max(0, b.slideCooldown - dt);
  ev.fall = 0;
  ev.drowning = 0;
  ev.enteredWater = false;
  ev.frameChanged = false;

  // A body on a ship that no longer exists continues in the world frame.
  const own = b.frame > FRAME_WORLD ? (ships[frameSlot(b.frame)] ?? null) : null;
  if (b.frame > FRAME_WORLD && !own) {
    b.frame = FRAME_WORLD;
    ev.frameChanged = true;
    endSlide(b);
  }

  // Look: yaw arrives in whichever frame the client believed it was in.
  let yaw = input.yaw;
  if (input.yawFrame !== b.frame) {
    const from = input.yawFrame > FRAME_WORLD ? (ships[frameSlot(input.yawFrame)]?.heading ?? 0) : 0;
    yaw += from - (own ? own.heading : 0);
  }
  b.yaw = wrapAngle(yaw);
  b.pitch = clamp(input.pitch, -PLAYER.maxPitch, PLAYER.maxPitch);

  if (b.mode === MODE_LAUNCH) {
    stepLaunch(b, ships, shapes, ev);
    stepBreath(b, dt, ev);
    return ev;
  }

  // Consume every C edge, even when permissions deny a slide.
  const crouchEdge = input.crouch && !b.crouchHeld;
  b.crouchHeld = input.crouch;
  if (b.slideRemaining > 0 && (!env.sprintAllowed || env.carrying || b.mode === MODE_SWIM || !b.grounded)) endSlide(b);
  let mx = clamp(input.mx, -1, 1);
  let mz = clamp(input.mz, -1, 1);
  const len = Math.hypot(mx, mz);
  if (len > 1) { mx /= len; mz /= len; }
  const s = Math.sin(b.yaw), c = Math.cos(b.yaw);
  const moveX = c * mx + s * mz, moveZ = -s * mx + c * mz;
  if (crouchEdge && b.sprintTime >= PLAYER.slideSprintRequired - 1e-6 && b.slideCooldown <= 1e-6
    && env.sprintAllowed && !env.carrying && b.mode === MODE_WALK && b.grounded) {
    b.slideRemaining = PLAYER.slideDuration;
    if (len > 0) b.slideYaw = Math.atan2(moveX, moveZ);
    b.sprintTime = 0;
  }
  const sliding = b.slideRemaining > 0;
  if (b.mode === MODE_SWIM) b.crouched = false;
  else if (sliding || input.crouch) b.crouched = true;
  else if (b.crouched && standingFits(b, ships, shapes)) b.crouched = false;

  // Jump from crouch first attempts to stand, and never jumps into a low ceiling.
  let jump = input.jump && !sliding;
  if (jump && b.crouched) {
    if (standingFits(b, ships, shapes)) b.crouched = false;
    else jump = false;
  }
  const sprint = !sliding && input.sprint && mz > 0 && env.sprintAllowed && !env.carrying
    && b.mode === MODE_WALK && b.grounded && !b.crouched && !jump;
  const speed = b.mode === MODE_SWIM ? PLAYER.swimSpeed : !b.grounded ? PLAYER.walkSpeed
    : b.crouched ? PLAYER.crouchSpeed : env.carrying ? PLAYER.carrySpeed : sprint ? PLAYER.sprintSpeed : PLAYER.walkSpeed;
  bodyWorld(b, ships, startWorld);
  let dx: number, dz: number;
  const slideDt = sliding ? Math.min(dt, b.slideRemaining) : 0;
  if (sliding) {
    const elapsed = PLAYER.slideDuration - b.slideRemaining;
    const slideSpeed = PLAYER.sprintSpeed + (PLAYER.crouchSpeed - PLAYER.sprintSpeed) * (elapsed + slideDt / 2) / PLAYER.slideDuration;
    dx = Math.sin(b.slideYaw) * slideSpeed * slideDt;
    dz = Math.cos(b.slideYaw) * slideSpeed * slideDt;
  } else {
    dx = moveX * speed * dt;
    dz = moveZ * speed * dt;
  }
  b.x += dx;
  b.z += dz;

  if (b.mode === MODE_SWIM) {
    b.vy = input.dive ? -PLAYER.diveSpeed : jump ? PLAYER.diveSpeed : PLAYER.floatUpSpeed;
    b.y += b.vy * dt;
    b.grounded = false;
  } else {
    if (b.grounded && jump) {
      b.vy = JUMP_VELOCITY;
      b.grounded = false;
      b.fallStartY = b.y;
    }
    b.vy -= PLAYER.gravity * dt;
    b.y += b.vy * dt;
  }
  bodyWorld(b, ships, endWorld);
  if (b.vy > 0) {
    const height = bodyHeight(b);
    let feet = headSweep(startWorld, endWorld, height, shapes);
    for (const ship of ships) {
      if (!ship || Math.abs(endWorld.x - ship.x) >= SHIP_NEAR || Math.abs(endWorld.z - ship.z) >= SHIP_NEAR) continue;
      worldToLocal(ship, startWorld.x, startWorld.y, startWorld.z, localStart);
      worldToLocal(ship, endWorld.x, endWorld.y, endWorld.z, localEnd);
      feet = Math.min(feet, headSweep(localStart, localEnd, height, SHIP_COLLIDERS));
    }
    if (feet < b.y) { b.y = feet; b.vy = 0; }
  }
  if (own) stepShipFrame(b, own, ships, shapes, ev);
  else stepWorldFrame(b, ships, shapes, ev);

  bodyWorld(b, ships, endWorld);
  // Test progress in world space so ship/world frame changes don't manufacture sprint.
  const heading = own?.heading ?? 0;
  const ws = Math.sin(heading), wc = Math.cos(heading);
  const requestX = dx * wc + dz * ws, requestZ = -dx * ws + dz * wc;
  const requestedSq = requestX * requestX + requestZ * requestZ;
  const progress = requestedSq > 1e-12
    ? ((endWorld.x - startWorld.x) * requestX + (endWorld.z - startWorld.z) * requestZ) / requestedSq : 0;
  b.sprinting = sprint && b.grounded && b.mode === MODE_WALK && progress >= 0.25;
  b.sprintTime = b.sprinting ? Math.min(PLAYER.slideSprintRequired, b.sprintTime + dt) : 0;
  if (b.sprinting) {
    const currentShip = b.frame > FRAME_WORLD ? ships[frameSlot(b.frame)] : null;
    b.slideYaw = wrapAngle(Math.atan2(requestX, requestZ) - (currentShip?.heading ?? 0));
  }
  if (sliding) {
    if (!b.grounded || b.mode === MODE_SWIM || progress < 0.25 || b.slideRemaining - slideDt <= 1e-6) endSlide(b);
    else b.slideRemaining -= slideDt;
  }
  if (b.mode === MODE_SWIM) b.crouched = false;
  else if (!b.slideRemaining && !input.crouch && b.crouched && standingFits(b, ships, shapes)) b.crouched = false;

  stepBreath(b, dt, ev);
  return ev;
}

function stepBreath(b: Body, dt: number, ev: StepEvents): void {
  const headUnder = b.mode === MODE_SWIM && b.y + 1.6 < -0.05;
  if (headUnder) {
    b.breath -= dt;
    if (b.breath < 0) {
      ev.drowning = Math.min(dt, -b.breath);
      b.breath = 0;
    }
  } else {
    b.breath = Math.min(PLAYER.breathSeconds, b.breath + (PLAYER.breathSeconds / PLAYER.breathRefillSeconds) * dt);
  }
}

/** Earliest entry into a rectangle during the supplied portion of a segment. */
function rectangleEntry(ax: number, az: number, dx: number, dz: number,
  minX: number, maxX: number, minZ: number, maxZ: number, lo: number, hi: number): number {
  if (Math.abs(dx) < 1e-12) {
    if (ax < minX || ax > maxX) return Infinity;
  } else {
    const a = (minX - ax) / dx, b = (maxX - ax) / dx;
    lo = Math.max(lo, Math.min(a, b));
    hi = Math.min(hi, Math.max(a, b));
  }
  if (Math.abs(dz) < 1e-12) {
    if (az < minZ || az > maxZ) return Infinity;
  } else {
    const a = (minZ - az) / dz, b = (maxZ - az) / dz;
    lo = Math.max(lo, Math.min(a, b));
    hi = Math.min(hi, Math.max(a, b));
  }
  return lo <= hi ? lo : Infinity;
}

function circleEntry(ax: number, az: number, dx: number, dz: number,
  x: number, z: number, radius: number, lo: number, hi: number): number {
  const ox = ax - x, oz = az - z, a = dx * dx + dz * dz;
  if (a < 1e-20) return ox * ox + oz * oz < radius * radius ? lo : Infinity;
  const b = ox * dx + oz * dz, c = ox * ox + oz * oz - radius * radius;
  const discriminant = b * b - a * c;
  if (discriminant <= 0) return Infinity;
  const root = Math.sqrt(discriminant);
  const enter = Math.max(lo, (-b - root) / a), exit = Math.min(hi, (-b + root) / a);
  return enter <= exit ? enter : Infinity;
}

/** Continuous sideways contact, including corner grazes missed by endpoint overlap. */
function launchSideEntry(from: Vec3, to: Vec3, c: Collider): number {
  const dy = to.y - from.y, low = c.y0 - PLAYER.height + CLEARANCE, high = c.y1 - CLEARANCE;
  let lo = 0, hi = 1;
  if (Math.abs(dy) < 1e-12) {
    if (from.y <= low || from.y >= high) return Infinity;
  } else {
    const a = (low - from.y) / dy, b = (high - from.y) / dy;
    lo = Math.max(lo, Math.min(a, b));
    hi = Math.min(hi, Math.max(a, b));
    if (lo > hi) return Infinity;
  }
  const dx = to.x - from.x, dz = to.z - from.z;
  if (c.kind === "cyl") return circleEntry(from.x, from.z, dx, dz, c.x, c.z, R + c.r, lo, hi);
  // A rounded box footprint is two rectangles and four corner discs.
  return Math.min(
    rectangleEntry(from.x, from.z, dx, dz, c.minX - R, c.maxX + R, c.minZ, c.maxZ, lo, hi),
    rectangleEntry(from.x, from.z, dx, dz, c.minX, c.maxX, c.minZ - R, c.maxZ + R, lo, hi),
    circleEntry(from.x, from.z, dx, dz, c.minX, c.minZ, R, lo, hi),
    circleEntry(from.x, from.z, dx, dz, c.minX, c.maxZ, R, lo, hi),
    circleEntry(from.x, from.z, dx, dz, c.maxX, c.minZ, R, lo, hi),
    circleEntry(from.x, from.z, dx, dz, c.maxX, c.maxZ, R, lo, hi),
  );
}

function launchTopEntry(from: Vec3, to: Vec3, shapes: readonly Collider[]): number {
  if (to.y >= from.y) return Infinity;
  let first = Infinity;
  for (const c of shapes) {
    if (from.y < c.y1 - CLEARANCE || to.y > c.y1) continue;
    const t = clamp((c.y1 - from.y) / (to.y - from.y), 0, 1);
    if (t < first && footprintOverlaps(from.x + (to.x - from.x) * t, from.z + (to.z - from.z) * t, c)) first = t;
  }
  return first;
}

function finishLaunch(b: Body, support: number, swimming: boolean, ev: StepEvents): void {
  b.y = support;
  b.vx = b.vy = b.vz = 0;
  b.mode = swimming ? MODE_SWIM : MODE_WALK;
  b.grounded = !swimming;
  b.fallStartY = support;
  ev.fall = 0;
  ev.enteredWater = swimming;
}

function stepLaunch(b: Body, ships: ShipPoses, shapes: readonly Collider[], ev: StepEvents): void {
  b.launchTicks++;
  // Gravity belongs to the outer clock, not the collision subdivision.
  b.vy -= SELF_CANNON.gravity * DT;
  const count = Math.max(1, Math.ceil(Math.hypot(b.vx, b.vy, b.vz) * DT / SELF_CANNON.maxStepDistance));
  const subdt = DT / count;
  for (let step = 0; step < count; step++) {
    let remaining = subdt;
    // At most one side interruption and one ceiling interruption before landing.
    while (remaining > 1e-12) {
      startWorld.x = b.x;
      startWorld.y = b.y;
      startWorld.z = b.z;
      endWorld.x = b.x + b.vx * remaining;
      endWorld.y = b.y + b.vy * remaining;
      endWorld.z = b.z + b.vz * remaining;
      const clampedX = clamp(endWorld.x, -BOUND, BOUND), clampedZ = clamp(endWorld.z, -BOUND, BOUND);
      const bounded = clampedX !== endWorld.x || clampedZ !== endWorld.z;
      endWorld.x = clampedX;
      endWorld.z = clampedZ;

      const previousGround = terrainHeight(startWorld.x, startWorld.z);
      let ground = terrainHeight(endWorld.x, endWorld.z);
      const horizontal = Math.hypot(endWorld.x - startWorld.x, endWorld.z - startWorld.z);
      if (horizontal > 0 && ground > endWorld.y + CLEARANCE
        && (b.vy > 0 || (ground > startWorld.y + CLEARANCE && ground - previousGround > PLAYER.maxSlope * horizontal))) {
        // A rising cliff is a wall, never a launch-time walking step.
        endWorld.x = startWorld.x;
        endWorld.z = startWorld.z;
        b.vx = b.vz = 0;
        ground = previousGround;
      }

      let top = launchTopEntry(startWorld, endWorld, shapes), topSlot = -1;
      let ceiling = Infinity, side = Infinity;
      if (b.vy > 0) {
        const y = headSweep(startWorld, endWorld, PLAYER.height, shapes);
        if (y < endWorld.y) ceiling = clamp((y - startWorld.y) / (endWorld.y - startWorld.y), 0, 1);
      }
      if (b.vx !== 0 || b.vz !== 0) {
        for (const c of shapes) side = Math.min(side, launchSideEntry(startWorld, endWorld, c));
      }
      for (let slot = 0; slot < ships.length; slot++) {
        const ship = ships[slot];
        if (!ship || Math.abs(endWorld.x - ship.x) >= SHIP_NEAR || Math.abs(endWorld.z - ship.z) >= SHIP_NEAR) continue;
        worldToLocal(ship, startWorld.x, startWorld.y, startWorld.z, localStart);
        worldToLocal(ship, endWorld.x, endWorld.y, endWorld.z, localEnd);
        const t = launchTopEntry(localStart, localEnd, SHIP_COLLIDERS);
        if (t < top) {
          top = t;
          topSlot = overDeck(localStart.x + (localEnd.x - localStart.x) * t, localStart.z + (localEnd.z - localStart.z) * t) ? slot : -1;
        }
        if (b.vy > 0) {
          const y = headSweep(localStart, localEnd, PLAYER.height, SHIP_COLLIDERS);
          if (y < localEnd.y) ceiling = Math.min(ceiling, clamp((y - localStart.y) / (localEnd.y - localStart.y), 0, 1));
        }
        if (b.vx !== 0 || b.vz !== 0) {
          for (const c of SHIP_COLLIDERS) side = Math.min(side, launchSideEntry(localStart, localEnd, c));
        }
      }

      if (b.vy <= 0 && endWorld.y <= ground && startWorld.y >= previousGround - CLEARANCE) {
        // Terrain is sampled at every bounded step; refine the first crossing.
        let lo = 0, hi = 1;
        for (let i = 0; i < 12; i++) {
          const t = (lo + hi) / 2;
          const y = startWorld.y + (endWorld.y - startWorld.y) * t;
          const h = terrainHeight(startWorld.x + (endWorld.x - startWorld.x) * t, startWorld.z + (endWorld.z - startWorld.z) * t);
          if (y > h) lo = t;
          else hi = t;
        }
        if (hi < top) { top = hi; topSlot = -1; }
      }

      let water = Infinity;
      if (b.vy <= 0 && endWorld.y <= PLAYER.surfaceFeetY) {
        const t = startWorld.y <= PLAYER.surfaceFeetY ? 0
          : (PLAYER.surfaceFeetY - startWorld.y) / (endWorld.y - startWorld.y);
        const x = startWorld.x + (endWorld.x - startWorld.x) * t, z = startWorld.z + (endWorld.z - startWorld.z) * t;
        let support = shapeSupport(x, PLAYER.surfaceFeetY - STEP, z, shapes, terrainHeight(x, z));
        for (const ship of ships) {
          if (!ship || Math.abs(x - ship.x) >= SHIP_NEAR || Math.abs(z - ship.z) >= SHIP_NEAR) continue;
          worldToLocal(ship, x, PLAYER.surfaceFeetY, z, tmp);
          support = shapeSupport(tmp.x, PLAYER.surfaceFeetY - STEP, tmp.z, SHIP_COLLIDERS, support);
        }
        if (support < PLAYER.swimDepth) water = t;
      }

      const contact = Math.min(top, water, ceiling, side);
      if (contact > 1) {
        b.x = endWorld.x;
        b.y = endWorld.y;
        b.z = endWorld.z;
        if (bounded) b.vx = b.vz = 0;
        break;
      }
      const t = contact === side && side < top && side < water && side < ceiling ? Math.max(0, contact - 1e-7) : contact;
      b.x = startWorld.x + (endWorld.x - startWorld.x) * t;
      b.y = startWorld.y + (endWorld.y - startWorld.y) * t;
      b.z = startWorld.z + (endWorld.z - startWorld.z) * t;
      if (top <= water && top <= ceiling && top <= side) {
        finishLaunch(b, b.y, false, ev);
        const ship = topSlot >= 0 ? ships[topSlot] : null;
        if (ship) { toShipFrame(b, ship, topSlot); ev.frameChanged = true; }
        return;
      }
      if (water <= ceiling && water <= side) {
        finishLaunch(b, PLAYER.surfaceFeetY, true, ev);
        return;
      }
      if (ceiling <= side) b.vy = 0;
      else {
        b.vx = b.vz = 0;
        // Also repair an initial overlap, without ordinary walking's STEP allowance.
        tmp.x = b.x; tmp.y = b.y; tmp.z = b.z;
        resolveSideways(tmp, shapes, PLAYER.height, 0);
        for (const ship of ships) {
          if (!ship || Math.abs(tmp.x - ship.x) >= SHIP_NEAR || Math.abs(tmp.z - ship.z) >= SHIP_NEAR) continue;
          worldToLocal(ship, tmp.x, tmp.y, tmp.z, tmp);
          resolveSideways(tmp, SHIP_COLLIDERS, PLAYER.height, 0);
          localToWorld(ship, tmp.x, tmp.y, tmp.z, tmp);
        }
        b.x = clamp(tmp.x, -BOUND, BOUND);
        b.z = clamp(tmp.z, -BOUND, BOUND);
      }
      if (bounded) b.vx = b.vz = 0;
      remaining *= 1 - contact;
    }
  }
}

function land(b: Body, support: number, ev: StepEvents): void {
  if (!b.grounded) ev.fall = Math.max(0, b.fallStartY - support);
  b.y = support;
  b.vy = 0;
  b.grounded = true;
  b.fallStartY = support;
}

function settleVertical(b: Body, support: number, ev: StepEvents): void {
  if (b.y <= support) {
    land(b, support, ev);
  } else if (b.grounded && b.vy <= 0 && b.y - support <= STEP) {
    // Walking downhill or down a step: stay glued to the ground.
    b.y = support;
    b.vy = 0;
  } else {
    if (b.grounded) b.fallStartY = b.y;
    b.grounded = false;
    if (b.y > b.fallStartY) b.fallStartY = b.y;
  }
}

function stepShipFrame(b: Body, ship: Pose, ships: ShipPoses, shapes: readonly Collider[], ev: StepEvents): void {
  tmp.x = b.x;
  tmp.y = b.y;
  tmp.z = b.z;
  resolveSideways(tmp, SHIP_COLLIDERS, bodyHeight(b));
  b.x = tmp.x;
  b.z = tmp.z;
  // A ship frame does not exempt the capsule from world overheads or nearby hulls.
  localToWorld(ship, b.x, b.y, b.z, endWorld);
  resolveSideways(endWorld, shapes, bodyHeight(b));
  for (const other of ships) {
    if (!other || other === ship || Math.abs(endWorld.x - other.x) >= SHIP_NEAR || Math.abs(endWorld.z - other.z) >= SHIP_NEAR) continue;
    worldToLocal(other, endWorld.x, endWorld.y, endWorld.z, tmp);
    resolveSideways(tmp, SHIP_COLLIDERS, bodyHeight(b));
    localToWorld(other, tmp.x, tmp.y, tmp.z, endWorld);
  }
  worldToLocal(ship, endWorld.x, endWorld.y, endWorld.z, tmp);
  b.x = tmp.x;
  b.z = tmp.z;

  if (!overDeck(b.x, b.z)) {
    // Stepped or jumped off the deck edge: continue in the world frame.
    toWorldFrame(b, ship);
    ev.frameChanged = true;
    stepWorldFrame(b, ships, shapes, ev);
    return;
  }
  // overDeck guarantees a hull top under the feet.
  settleVertical(b, deckSupport(b.x, b.y, b.z), ev);
}

function stepWorldFrame(b: Body, ships: ShipPoses, shapes: readonly Collider[], ev: StepEvents): void {
  tmp.x = b.x;
  tmp.y = b.y;
  tmp.z = b.z;
  resolveSideways(tmp, shapes, bodyHeight(b));

  // Ship hulls are solid for world-frame bodies too (swimmers, pier walkers).
  let deck = -Infinity;
  let deckSlot = -1;
  for (let i = 0; i < ships.length; i++) {
    const ship = ships[i];
    if (!ship || Math.abs(tmp.x - ship.x) >= SHIP_NEAR || Math.abs(tmp.z - ship.z) >= SHIP_NEAR) continue;
    worldToLocal(ship, tmp.x, tmp.y, tmp.z, tmp);
    resolveSideways(tmp, SHIP_COLLIDERS, bodyHeight(b));
    if (overDeck(tmp.x, tmp.z)) {
      const d = deckSupport(tmp.x, b.y, tmp.z);
      if (d > deck) {
        deck = d;
        deckSlot = i;
      }
    }
    localToWorld(ship, tmp.x, tmp.y, tmp.z, tmp);
  }
  b.x = clamp(tmp.x, -BOUND, BOUND);
  b.z = clamp(tmp.z, -BOUND, BOUND);

  const ground = terrainHeight(b.x, b.z);
  const world = shapeSupport(b.x, b.y, b.z, shapes, ground);
  if (deck > world && b.y <= deck + 0.01 && b.mode === MODE_WALK) {
    // Landed on (or walked onto) a deck: switch to ship-relative movement.
    settleVertical(b, deck, ev);
    const ship = ships[deckSlot];
    if (b.grounded && ship) {
      toShipFrame(b, ship, deckSlot);
      ev.frameChanged = true;
    }
    return;
  }

  const support = Math.max(world, deck);
  if (b.mode === MODE_SWIM) {
    if (world >= PLAYER.swimDepth && b.y <= world + STEP) {
      // Waded ashore.
      b.mode = MODE_WALK;
      b.y = world;
      b.vy = 0;
      b.grounded = true;
      b.fallStartY = world;
      return;
    }
    const minY = Math.max(PLAYER.surfaceFeetY - PLAYER.maxDiveDepth, ground + 0.05);
    if (b.y > PLAYER.surfaceFeetY) b.y = PLAYER.surfaceFeetY;
    if (b.y < minY) b.y = minY;
    return;
  }

  if (support < PLAYER.swimDepth && b.y <= PLAYER.surfaceFeetY) {
    b.mode = MODE_SWIM;
    b.vy = 0;
    b.grounded = false;
    b.fallStartY = PLAYER.surfaceFeetY;
    ev.enteredWater = true;
    if (b.y < PLAYER.surfaceFeetY - 1.5) b.y = PLAYER.surfaceFeetY - 1.5;
    return;
  }
  settleVertical(b, support, ev);
}

/** True when the body may use the ladder (swimming near the ladder bottom). */
export function nearLadder(b: Body, ship: Pose, ladderBottom: Vec3, range: number): boolean {
  if (b.frame !== FRAME_WORLD || b.mode !== MODE_SWIM) return false;
  localToWorld(ship, ladderBottom.x, ladderBottom.y, ladderBottom.z, tmp);
  const dx = b.x - tmp.x;
  const dz = b.z - tmp.z;
  const dy = b.y + 0.9 - tmp.y;
  return dx * dx + dz * dz + dy * dy * 0.25 <= range * range;
}
